import { getDateNDaysBack } from '@helios/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import _ from 'lodash'
import { AccountInfo } from '../../focused'

export const sessionFeatureKey = 'session'

export enum PeriodLength {
  Last30Days = 'Last 30 days',
  Last100Days = 'Last 100 days',
  Last1Year = 'Last 1 year',
  Last2Years = 'Last 2 years',
  FromDate = 'From date',
}

/**
 * Convert period length to date range
 */
export function periodLengthToDate(
  periodLength: PeriodLength,
  fromDate: Date = getDateNDaysBack(100),
): Date {
  switch (periodLength) {
    case PeriodLength.Last30Days:
      return getDateNDaysBack(30)
    case PeriodLength.Last100Days:
      return getDateNDaysBack(100)
    case PeriodLength.Last1Year:
      return getDateNDaysBack(365)
    case PeriodLength.Last2Years:
      return getDateNDaysBack(365 * 2)
    case PeriodLength.FromDate:
      return fromDate
  }
}

export interface UsersInSession {
  userId: string
  username: string
}

export interface AuxiliaryInfo {
  LOAD_WINDOW_START_DATE: Date
  BASIS_NAME?: string
  BASIS_ID?: number
  NEED_REVIEW?: boolean
  REQUEST_IDS?: CreatedRequestDetails[]
}

export interface CreatedRequestDetails {
  requestId: number
  accountId: number
  reconDate: Date
}

/**
 * Mirrors the backend's `SessionKind` (`libs/common-models/.../sessions/SessionKind.kt` in the
 * helios backend repo). Already present on every session payload from `GET /sessions/external` —
 * this type just gives the frontend a name for a field it was previously ignoring. The backend
 * derives the kind itself when a session is created, looking each account's External Reference
 * Source up via the accounts service, independently of the create-session request body — so the
 * frontend cannot influence it and must treat it as read-only server state.
 */
export type SessionKind = 'CW' | 'ENFN'

export type Session = {
  loadedAccountIds: number[]
  createdDateTime: Date
  creationUserId: number
  id: string
  reconDate: Date
  users?: UsersInSession[]
  auxiliaryInfo?: AuxiliaryInfo
  loadedAccounts?: AccountInfo[]
  kind?: SessionKind
  /** State */
  isAffectedByJobRun?: boolean
  isHplAccount?: boolean
  isEnfusionAccount?: boolean
  loadedForNonDefaultBasis?: boolean
}

export type SessionState = {
  accountCommands?: AccountCommandCount[]
  sumOfCommands?: number
  isPinned?: boolean
}

export interface AccountCommandCount {
  accountId: number
  commandCount: number
}

export type SessionWithState = Session & SessionState

export const sessionInitialState: SessionRedux = {
  activeSession: undefined,
  sessions: [],
  actualReconDate: undefined,
}

export type SessionRedux = {
  activeSession: SessionWithState | undefined
  sessions: SessionWithState[]
  actualReconDate: Date | undefined
  isCreating?: boolean
  selectedReconDate?: Date
  isSwitching?: boolean
}

export const sessionSlice = createSlice({
  name: sessionFeatureKey,
  initialState: sessionInitialState,
  reducers: {
    setActiveSession: (
      oldState,
      action: PayloadAction<{ session: SessionWithState }>,
    ) => ({
      ...oldState,
      activeSession: action.payload.session,
    }),
    inactivateSession: (
      oldState,
      action: PayloadAction<{ sessionId: string }>,
    ) => {
      if (
        oldState.activeSession != null &&
        oldState.activeSession.id === action.payload.sessionId
      )
        return {
          ...oldState,
          activeSession: undefined,
        }
      return {
        ...oldState,
      }
    },
    setSessions: (
      oldState,
      action: PayloadAction<{ sessions: SessionWithState[] }>,
    ) => ({
      ...oldState,
      sessions: action.payload.sessions.map(x => ({
        ...x,
        accountCommands: x.accountCommands ?? [],
      })),
    }),
    toggleSessionPin: (
      oldState,
      action: PayloadAction<{ sessionId: string; isPinned: boolean }>,
    ) => ({
      ...oldState,
      sessions: oldState.sessions.map(session => {
        if (action.payload.sessionId === session.id) {
          return {
            ...session,
            isPinned: action.payload.isPinned,
          }
        }
        return session
      }),
    }),
    addSession: (
      oldState,
      action: PayloadAction<{ session: SessionWithState }>,
    ) => {
      // Remove the old session
      const sessions = oldState.sessions.filter(
        x => x.id !== action.payload.session.id,
      )
      // Add the new session
      return {
        ...oldState,
        sessions: [...sessions, action.payload.session],
      }
    },
    removeSession: (
      oldState,
      action: PayloadAction<{ sessionId: string }>,
    ) => ({
      ...oldState,
      sessions: oldState.sessions.filter(
        oldSession => oldSession.id !== action.payload.sessionId,
      ),
    }),

    setReconDate: (oldState, action: PayloadAction<{ reconDate: Date }>) => ({
      ...oldState,
      actualReconDate: action.payload.reconDate,
    }),

    setSelectedReconDate: (
      oldState,
      action: PayloadAction<{ selectedReconDate: Date }>,
    ) => ({
      ...oldState,
      selectedReconDate: action.payload.selectedReconDate,
    }),

    setIsCreatingSession: (
      oldState,
      action: PayloadAction<{ isCreating: boolean }>,
    ) => ({
      ...oldState,
      isCreating: action.payload.isCreating,
    }),
    setIsSwitchingSession: (
      oldState,
      action: PayloadAction<{ isSwitching: boolean }>,
    ) => ({
      ...oldState,
      isSwitching: action.payload.isSwitching,
    }),
    setIsAffectedByJobRun: (
      oldState,
      action: PayloadAction<{
        sessionIds: string[]
      }>,
    ) => ({
      ...oldState,
      sessions: oldState.sessions.map(session => {
        if (action.payload.sessionIds.includes(session.id)) {
          return {
            ...session,
            isAffectedByJobRun: true,
          }
        }
        return session
      }),
    }),

    updateSessionAuxiliaryInfo: (
      oldState,
      action: PayloadAction<{
        sessionId: string
        auxiliaryInfo: Partial<AuxiliaryInfo>
      }>,
    ) => {
      //using .cloneDeep because it preserves the original data types
      const state = _.cloneDeep(oldState) as SessionRedux
      const session = state.sessions.find(
        oldSession => oldSession.id === action.payload.sessionId,
      )

      if (session) {
        session.auxiliaryInfo = {
          ...session.auxiliaryInfo,
          ...action.payload.auxiliaryInfo,
        } as AuxiliaryInfo
      }

      if (state.activeSession?.id === action.payload.sessionId) {
        state.activeSession.auxiliaryInfo = {
          ...state.activeSession.auxiliaryInfo,
          ...action.payload.auxiliaryInfo,
        } as AuxiliaryInfo
      }

      return {
        ...state,
        sessions: state.sessions,
      }
    },

    setSessionCommands: (
      oldState,
      action: PayloadAction<{
        sessionId: string
        commands: AccountCommandCount[]
      }>,
    ) => {
      //using .cloneDeep because it preserves the original data types
      const state = _.cloneDeep(oldState) as SessionRedux
      const session = state.sessions.find(
        oldSession => oldSession.id == action.payload.sessionId,
      )
      const sumOfCommands = calculateSumOfCommands(action.payload.commands)

      if (session) {
        session.accountCommands = action.payload.commands
        session.sumOfCommands = sumOfCommands
      }

      if (state.activeSession?.id === action.payload.sessionId) {
        state.activeSession.accountCommands = action.payload.commands
        state.activeSession.sumOfCommands = sumOfCommands
      }

      return {
        ...state,
        sessions: state.sessions,
      }
    },
  },
})

/**
 * Calculates the sum of all commands in the session.
 */
function calculateSumOfCommands(commands: AccountCommandCount[]) {
  return commands.reduce((sum, curr) => {
    return sum + curr.commandCount
  }, 0)
}

/**
 * These actions should only be used within the SessionsService and tests.
 */
export const {
  setActiveSession,
  inactivateSession,
  setSessions,
  addSession,
  removeSession,
  setReconDate,
  toggleSessionPin,
  setIsAffectedByJobRun,
  setSessionCommands,
  setIsCreatingSession,
  setIsSwitchingSession,
  setSelectedReconDate,
  updateSessionAuxiliaryInfo,
} = sessionSlice.actions

export default sessionSlice.reducer
